import { useMemo } from 'react';

import { CostChannel } from '@sg/graphql-schema';

import {
  type BundleSelectProduct,
  useBundleCategories,
  useBundleContent,
} from '@order/components-menu';

import { useLastInteractedStore } from '../../../../context';
import {
  useBundleDeliveryMenuQuery,
  useBundlePickupMenuQuery,
} from '../../graphql/BundleMenu.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves everything the bundle screen needs from the network: its Contentful
 * entry, and the menu products of the categories that entry points at.
 */
export const useBundleRemoteData = () => {
  const lastInteractedStore = useLastInteractedStore();

  const { restaurantId, restaurantSlug, addressId } = lastInteractedStore ?? {};

  const isDeliveryStore = Boolean(addressId);

  const { content, fetching: isFetchingContent } = useBundleContent();

  const [pickupMenuResponse] = useBundlePickupMenuQuery({
    variables: { id: restaurantSlug ?? '', costChannel: CostChannel.PickupCostChannel },
    requestPolicy: 'cache-and-network',
    pause: isDeliveryStore || !restaurantSlug,
  });

  const [deliveryMenuResponse] = useBundleDeliveryMenuQuery({
    variables: {
      input: { addressId, useCartRestaurant: true },
      costChannel: CostChannel.DeliveryCostChannel,
    },
    requestPolicy: 'cache-and-network',
    pause: !addressId,
  });

  const restaurant = useBundleRestaurant({
    isDeliveryStore,
    pickupRestaurant: pickupMenuResponse.data?.restaurant,
    deliveryLocation: deliveryMenuResponse.data?.locationByAddress,
  });

  const categories = useBundleCategories({
    categories: restaurant?.menu.categories,
    categoryIds: content.categoryIds,
  });

  // ─── Derived ─────────────────────────────────────────────────────────

  const products = useMemo(
    () => categories.flatMap((category) => category.products).map(toBundleSelectProduct),
    [categories],
  );

  const productsBySlug = useMemo(
    () => new Map(products.map((product) => [product.slug, product])),
    [products],
  );

  const hasLocation = Boolean(restaurantId);

  // NOTE: Only the first fetch counts, so refreshing the restaurant in the background
  //       doesn't tear down the bundle while products are being added.
  const isLoadingPickupMenu = pickupMenuResponse.fetching && !pickupMenuResponse.data;
  const isLoadingDeliveryMenu = deliveryMenuResponse.fetching && !deliveryMenuResponse.data;
  const isLoading = isFetchingContent || isLoadingPickupMenu || isLoadingDeliveryMenu;

  // ─────────────────────────────────────────────────────────────────────

  return {
    content,
    products,
    productsBySlug,

    // NOTE: Read from the loaded restaurant, since the last interacted store
    //       drops the slug for delivery addresses.
    restaurantSlug: restaurant?.slug,
    isDelivery: isDeliveryStore,
    hasLocation,
    isLoading,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function useBundleRestaurant(params: Params) {
  const { isDeliveryStore, pickupRestaurant, deliveryLocation } = params;

  return useMemo(() => {
    if (!isDeliveryStore) return pickupRestaurant;

    if (deliveryLocation?.__typename !== 'Location') return;

    return deliveryLocation.restaurant;
  }, [deliveryLocation, isDeliveryStore, pickupRestaurant]);
}

function toBundleSelectProduct(product: MenuProduct): BundleSelectProduct {
  return {
    id: product.id,
    slug: product.slug,
    name: product.name,
    cost: product.cost,
    calories: product.calories,
    imageUrl: product.asset?.url ?? '',
    isOutOfStock: product.outOfStock,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type BundleRestaurant = NonNullable<
  ReturnType<typeof useBundlePickupMenuQuery>[0]['data']
>['restaurant'];

type BundleDeliveryLocation = NonNullable<
  ReturnType<typeof useBundleDeliveryMenuQuery>[0]['data']
>['locationByAddress'];

type Params = {
  isDeliveryStore: boolean;
  pickupRestaurant: BundleRestaurant | undefined;
  deliveryLocation: BundleDeliveryLocation | undefined;
};

type MenuProduct = {
  id: string;
  slug: string;
  name: string;
  cost: number;
  calories: number;
  outOfStock: boolean;
  asset?: { url: string } | null;
};
